import { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import * as d3 from "d3";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import type { RootState } from "../store";
import Kundenbewertungen from "./Kundenbewertungen";

type ChartPoint = {
  month: string;
  value: number;
};

type ProcessStep = {
  title: string;
  description: string;
};

const CHART_VALUES = [22, 28, 34, 46, 58, 64];

function GenerationChart({ data }: { data: ChartPoint[] }) {
  const svgRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    if (!svgRef.current) return;

    const width = 320;
    const height = 180;
    const margin = { top: 20, right: 20, bottom: 30, left: 36 };

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    svg.attr("viewBox", `0 0 ${width} ${height}`);

    const x = d3
      .scalePoint<string>()
      .domain(data.map((d) => d.month))
      .range([margin.left, width - margin.right])
      .padding(0.4);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => d.value)! + 10])
      .nice()
      .range([height - margin.bottom, margin.top]);

    const area = d3
      .area<ChartPoint>()
      .x((d) => x(d.month)!)
      .y0(height - margin.bottom)
      .y1((d) => y(d.value))
      .curve(d3.curveMonotoneX);

    const line = d3
      .line<ChartPoint>()
      .x((d) => x(d.month)!)
      .y((d) => y(d.value))
      .curve(d3.curveMonotoneX);

    svg
      .append("path")
      .datum(data)
      .attr("fill", "rgba(47, 111, 95, 0.18)")
      .attr("d", area);

    svg
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", "#2f6f5f")
      .attr("stroke-width", 2.5)
      .attr("d", line);

    svg
      .append("g")
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x).tickSizeOuter(0))
      .selectAll("text")
      .attr("fill", "#4b5b5a")
      .attr("font-size", 11);

    svg
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(4))
      .selectAll("text")
      .attr("fill", "#4b5b5a")
      .attr("font-size", 11);

    svg.selectAll(".domain").attr("stroke", "var(--chart-axis)");
    svg.selectAll(".tick line").attr("stroke", "var(--chart-grid)");
  }, [data]);

  return <svg ref={svgRef} style={{ width: "100%", height: 200 }} />;
}

export default function Home() {
  const { t } = useTranslation();
  const energy = useSelector((state: RootState) => state.energy);

  const monthLabels = useMemo<string[]>(() => {
    const value = t("home.stats.chart.months", { returnObjects: true }) as string[] | string;
    if (Array.isArray(value)) {
      return value.map((item: string) => String(item));
    }
    if (typeof value === "string") {
      const split = value
        .split(",")
        .map((item: string) => item.trim())
        .filter(Boolean);
      if (split.length > 0) return split;
    }
    return ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
  }, [t]);
  const chartData = useMemo(
    () => monthLabels.map((month, index) => ({ month, value: CHART_VALUES[index] ?? 0 })),
    [monthLabels]
  );

  const processSteps = useMemo(() => {
    const value = t("home.process.items", { returnObjects: true });
    return Array.isArray(value) ? (value as ProcessStep[]) : [];
  }, [t]);

  const benefitItems = useMemo(() => {
    const value = t("home.benefits.items", { returnObjects: true });
    return Array.isArray(value) ? (value as { title: string; description: string }[]) : [];
  }, [t]);

  return (
    <Container sx={{ py: { xs: 4, md: 6 } }} id="top">
      <Box className="hero-shell" sx={{ p: { xs: 4, md: 6 }, position: "relative" }}>
        <Box className="hero-glow" />
        <Grid container spacing={4} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
          <Grid item xs={12} md={7}>
            <Stack spacing={2.5}>
              <Typography variant="h1" sx={{ wordBreak: "break-word", hyphens: "auto", fontSize: { xs: "2rem", sm: "3rem", md: "3.5rem" } }}>{t("home.hero.title")}</Typography>
              <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 520, hyphens: "auto" }}>
                {t("home.hero.subtitle")}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
                <Button component={RouterLink} to="/kontakt" variant="contained" color="secondary">
                  {t("home.hero.cta")}
                </Button>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                background: "rgba(255, 255, 255, 0.14)",
                border: "1px solid rgba(255,255,255,0.22)",
                backdropFilter: "blur(8px)",
                p: { xs: 3, sm: 4 },
                color: "#fefbf5",
              }}
            >
              <Stack spacing={2}>
                <Typography variant="h3" sx={{ wordBreak: "break-word", hyphens: "auto" }}>{energy.region}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {t("home.region.label")}
                </Typography>
                <Typography variant="h2" sx={{ wordBreak: "break-word", hyphens: "auto", fontSize: { xs: "1.75rem", sm: "2.25rem" } }}>{t(`home.gridStatus.${energy.gridStatusId}`)}</Typography>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.25)" }} />
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <InsightsRoundedIcon fontSize="small" />
                    <Typography variant="body2">{t("home.heroHighlights.item1")}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MapRoundedIcon fontSize="small" />
                    <Typography variant="body2">{t("home.heroHighlights.item2")}</Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: { xs: 5, md: 7 } }} id="ueber-uns">
        <Stack spacing={1} sx={{ maxWidth: 720 }}>
          <Typography className="section-label">{t("home.about.sectionLabel")}</Typography>
          <Typography variant="h2">{t("home.about.title")}</Typography>
          <Typography variant="body1" color="text.secondary">
            {t("home.about.body")}
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ mt: { xs: 5, md: 7 } }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, border: "1px solid var(--border)", background: "var(--surface)" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box sx={{ maxWidth: 640 }}>
              <Typography className="section-label">{t("home.servicesTeaser.sectionLabel")}</Typography>
              <Typography variant="h2">{t("home.servicesTeaser.title")}</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                {t("home.servicesTeaser.body")}
              </Typography>
            </Box>
            <Button component={RouterLink} to="/leistungen" variant="contained" color="secondary">
              {t("home.servicesTeaser.cta")}
            </Button>
          </Stack>
        </Paper>
      </Box>

      <Box sx={{ mt: { xs: 5, md: 7 } }}>
        <Typography className="section-label">{t("home.stats.sectionLabel")}</Typography>
        <Typography variant="h2" sx={{ mt: 1, mb: 3 }}>
          {t("home.stats.title")}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={5}>
            <Card elevation={0} sx={{ border: "1px solid var(--border)", height: "100%" }}>
              <CardContent>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("home.stats.chart.label")}
                  </Typography>
                  <Typography variant="h3">{t("home.stats.chart.title")}</Typography>
                </Stack>
                <Box sx={{ mt: 2 }}>
                  <GenerationChart data={chartData} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={7}>
            <Card elevation={0} sx={{ border: "1px solid var(--border)", height: "100%" }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("home.stats.economics.label")}
                  </Typography>
                  <Typography variant="h3">{t("home.stats.economics.title")}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("home.stats.economics.body")}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          {energy.stats.map((stat) => (
            <Grid item xs={12} md={4} key={stat.id}>
              <Card elevation={0} sx={{ backgroundColor: "var(--surface)", border: "1px solid var(--border)" }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t(`home.stats.cards.${stat.id}.label`)}
                  </Typography>
                  <Typography variant="h2" sx={{ mt: 1 }}>
                    {t(`home.stats.cards.${stat.id}.value`)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(`home.stats.cards.${stat.id}.helper`)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mt: { xs: 5, md: 7 } }} id="vorteile">
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography className="section-label">{t("home.benefits.sectionLabel")}</Typography>
          <Typography variant="h2">{t("home.benefits.title")}</Typography>
        </Box>
        <Grid container spacing={3}>
          {benefitItems.map((item) => (
            <Grid item xs={12} md={6} key={item.title}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: 4,
                  border: "none",
                  backgroundColor: "var(--surface)",
                  textAlign: "center",
                  borderRadius: 3,
                }}
              >
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                  {item.title}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {item.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mt: { xs: 5, md: 7 }, py: { xs: 1.5, md: 2.5 } }}>
        <Kundenbewertungen id="kundenbewertungen" limit={3} showCta />
      </Box>

      <Box sx={{ mt: { xs: 5, md: 7 } }} id="ablauf">
        <Stack spacing={1}>
          <Typography className="section-label">{t("home.process.sectionLabel")}</Typography>
          <Typography variant="h2" sx={{ mb: 3, p: 2 }}>
            {t("home.process.title")}
          </Typography>
        </Stack>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={5}>
            <Box
              component="img"
              src="/assets/van_front_right_view_1772340994888.png"
              alt="RSS-Energie Firmenwagen beim Kunden"
              sx={{ width: "100%", borderRadius: 4, objectFit: "cover", display: "block", aspectRatio: "1" }}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <Stack spacing={4}>
              {processSteps.map((step, index) => (
                <Stack direction="row" spacing={3} key={step.title} alignItems="flex-start">
                  <Typography
                    sx={{
                      fontSize: "4rem",
                      fontWeight: 800,
                      color: "secondary.main",
                      lineHeight: 1,
                      minWidth: "60px",
                    }}
                  >
                    {index + 1}
                  </Typography>
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {step.description}
                    </Typography>
                  </Box>
                </Stack>
              ))}
              <Typography variant="body1" sx={{ mt: 2 }}>
                {t("home.process.conclusion")}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: { xs: 5, md: 7 } }} id="partner">
        <Typography variant="h3" sx={{ mb: 2 }}>
          {t("home.partner.title")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 900 }}>
          {t("home.partner.body")}
        </Typography>
      </Box>

      <Box sx={{ mt: { xs: 5, md: 7 } }} id="termin-vereinbaren">
        <Paper
          elevation={0}
          sx={{ p: { xs: 3, md: 4 }, border: "1px solid var(--border)", background: "var(--surface)" }}
        >
          <Stack spacing={2} sx={{ maxWidth: 640 }}>
            <Typography className="section-label">{t("home.appointment.sectionLabel")}</Typography>
            <Typography variant="h2">{t("home.appointment.title")}</Typography>
            <Typography variant="body1" color="text.secondary">
              {t("home.appointment.body")}
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              component={RouterLink}
              to="/termin-vereinbaren"
              sx={{ alignSelf: "flex-start" }}
            >
              {t("home.appointment.cta")}
            </Button>
          </Stack>
        </Paper>
      </Box>
      <Box sx={{ mt: { xs: 5, md: 7 }, pb: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, border: "1px solid var(--border)", background: "var(--surface)" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box sx={{ maxWidth: 640 }}>
              <Typography className="section-label">{t("home.contactTeaser.sectionLabel")}</Typography>
              <Typography variant="h2">{t("home.contactTeaser.title")}</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                {t("home.contactTeaser.body")}
              </Typography>
            </Box>
            <Button component={RouterLink} to="/kontakt" variant="contained" color="secondary">
              {t("home.contactTeaser.cta")}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
}
